/* 
Copyright (c) Karsten Jeske and John Firth
This source code is licensed under the proprietary license found in the
LICENSE file in the root directory of this source tree.
*/

import {
  userParametersSchemaV1,
  UserParametersV1,
} from "features/userParameters/UserParametersSchemata"
import { Sentry } from "services/sentry"
import * as XLSX from "xlsx"
import { DeepReadonly } from "utils/readonly"

interface ZodErrorObject {
  _errors?: string[]
  [key: string]: ZodErrorObject | string[] | undefined
}

// AIDEV-NOTE: Use readonly workbook parsing contracts to satisfy strict correctness linting without changing parse behavior.

type CellValue = string | number | DeepReadonly<Date> | null
type SheetRow = readonly CellValue[]
type SheetData = readonly SheetRow[]

const parseMonthYear = (val: DeepReadonly<CellValue>): { month: number; year: number } | null => {
  if (typeof val === "string") {
    if (val.includes("/")) {
      const parts = val.split("/")
      if (parts.length === 2) {
        const month = parseInt(parts[0], 10)
        const year = parseInt(parts[1], 10)
        if (!isNaN(month) && !isNaN(year)) {
          return { month, year }
        }
      }
    } else if (val.includes("-")) {
      const date = new Date(val)
      if (!isNaN(date.getTime())) {
        return { month: date.getMonth() + 1, year: date.getFullYear() }
      }
    }
  } else if (val instanceof Date) {
    if (!isNaN(val.getTime())) {
      return { month: val.getMonth() + 1, year: val.getFullYear() }
    }
  }
  return null
}

export const createLabelLookup = (rows: DeepReadonly<SheetData>): Record<string, CellValue> => {
  const lookup: Record<string, CellValue> = {}
  for (const row of rows) {
    if (typeof row[0] === "string") {
      lookup[row[0]] = row[1] ?? null
    }
  }
  return lookup
}

export const parseGeneralParameters = (data: DeepReadonly<SheetData>) => {
  const lookup = createLabelLookup(data)

  const requiredFields = [
    "Initial Portfolio Total",
    "Retirement Start",
    "Retirement Horizon",
    "Final Value Target",
    "Inflation Rate",
    "CAPE Model",
    "Allocation Mode",
  ]

  // Check if each required field exists as a key in the lookup table
  const missingFields = requiredFields.filter(field => !(field in lookup))

  // If any required fields are missing, throw an error
  if (missingFields.length > 0) {
    throw Error(`Missing required fields in General Parameters sheet: ${missingFields.join(", ")}.`)
  }

  return {
    initialPortfolioDollars: lookup["Initial Portfolio Total"],
    retirementStart: parseMonthYear(lookup["Retirement Start"]),
    retirementHorizonMonths: lookup["Retirement Horizon"],
    finalValuePctOfInitial: lookup["Final Value Target"],
    inflationPerAnnumPct: lookup["Inflation Rate"],
    initialConsumptionPerAnnumDollars: lookup["Initial Consumption Amount"] ?? undefined,
    cape: lookup["CAPE Model"],
    allocationMode: lookup["Allocation Mode"],
    glidePathLengthMonths: lookup["Glide Path Length"],
  }
}

export const parseAssetAllocations = (data: DeepReadonly<SheetData>) => {
  const [groupHeaderRow, headerRow, ...dataRows] = data

  // check for required group headers
  const requiredGroupHeaders = ["Allocation", "Fama French Factors"]

  const missingGroupHeaders = requiredGroupHeaders.filter(
    header => !groupHeaderRow.includes(header)
  )
  if (missingGroupHeaders.length > 0) {
    throw new Error(
      `Missing required first row header(s) in Assets sheet: ${missingGroupHeaders.join(", ")}`
    )
  }

  // check for required headers
  const requiredHeaders = [
    "Name",
    "Expense Ratio",
    "Stocks: S&P 500",
    "Stocks: Non-US",
    "Cash: 3m U.S. T-bills",
    "Bonds: 10y U.S. Treasury",
    "Bonds: 30y U.S. Treasury",
    "Commodities",
    "Gold",
    "Small Stocks (SMB)",
    "Value Stocks (HML)",
  ]
  const missingHeaders = requiredHeaders.filter(header => !headerRow.includes(header))
  if (missingHeaders.length > 0) {
    throw new Error(
      `Missing required second row header(s) in Assets sheet: ${missingHeaders.join(", ")}`
    )
  }

  // parse asset data rows
  const assetsData = dataRows.map((row: DeepReadonly<SheetRow>) => {
    const rowObj: Record<string, CellValue> = {}
    headerRow.forEach((col: DeepReadonly<CellValue>, index) => {
      if (typeof col === "string") {
        rowObj[col] = row[index]
      }
    })
    return rowObj
  })

  const initialRow = assetsData.find(
    (row: DeepReadonly<Record<string, CellValue>>) => row.Name?.toString() === "Initial"
  )
  const finalRow = assetsData.find(
    (row: DeepReadonly<Record<string, CellValue>>) => row.Name?.toString() === "Final"
  )

  if (!initialRow) {
    throw new Error("'Initial' row not found in Assets sheet.")
  }

  const initialAssets = {
    expenseRatioPerAnnumPct: initialRow["Expense Ratio"],
    allocation: {
      spxTrPct: initialRow["Stocks: S&P 500"],
      worldExUsEquitiesPct: initialRow["Stocks: Non-US"],
      cashPct: initialRow["Cash: 3m U.S. T-bills"],
      bm10yPct: initialRow["Bonds: 10y U.S. Treasury"],
      bm30yPct: initialRow["Bonds: 30y U.S. Treasury"],
      commoditiesPct: initialRow.Commodities,
      goldPct: initialRow.Gold,
      customSeries1Pct: 0,
      customSeries2Pct: 0,
    },
    famaFrenchFactors: {
      smallStocksPct: initialRow["Small Stocks (SMB)"],
      valueStocksPct: initialRow["Value Stocks (HML)"],
    },
  }

  let finalAssets = undefined
  if (finalRow) {
    finalAssets = {
      expenseRatioPerAnnumPct: finalRow["Expense Ratio"],
      allocation: {
        spxTrPct: finalRow["Stocks: S&P 500"],
        worldExUsEquitiesPct: finalRow["Stocks: Non-US"],
        cashPct: finalRow["Cash: 3m U.S. T-bills"],
        bm10yPct: finalRow["Bonds: 10y U.S. Treasury"],
        bm30yPct: finalRow["Bonds: 30y U.S. Treasury"],
        commoditiesPct: finalRow.Commodities,
        goldPct: finalRow.Gold,
        customSeries1Pct: 0,
        customSeries2Pct: 0,
      },
      famaFrenchFactors: {
        smallStocksPct: finalRow["Small Stocks (SMB)"],
        valueStocksPct: finalRow["Value Stocks (HML)"],
      },
    }
  }

  return { initialAssets, finalAssets }
}

export const parseCashFlows = (data: DeepReadonly<SheetData>) => {
  const cashFlowHeader = data[0] as readonly string[]
  const requiredHeaders = ["Name", "Amount", "Frequency", "Inflation Adjusted", "Start", "End"]
  const missingHeaders = requiredHeaders.filter(header => !cashFlowHeader.includes(header))

  if (missingHeaders.length > 0) {
    throw Error(
      `Missing required column header(s) in Cash Flows sheet: ${missingHeaders.join(", ")}.`
    )
  }

  const headerIndexMap = Object.fromEntries(
    cashFlowHeader.map((col, idx) => [col, idx] as const)
  ) as Record<string, number>
  const cashFlowRows = data.slice(1)

  const parseInflationAdjusted = (val: string) => {
    const valLower = val.trim().toLowerCase()
    if (valLower === "yes") return true
    if (valLower === "no") return false
    return null
  }

  return cashFlowRows.map((row: DeepReadonly<SheetRow>) => {
    const name = row[headerIndexMap.Name]
    const amount = row[headerIndexMap.Amount]
    const frequency = row[headerIndexMap.Frequency]
    const inflationAdjusted = parseInflationAdjusted(
      String(row[headerIndexMap["Inflation Adjusted"]])
    )
    const start = parseMonthYear(row[headerIndexMap.Start])
    const endVal = row[headerIndexMap.End]
    const end = endVal == null || endVal === "" ? undefined : parseMonthYear(endVal)

    return {
      name,
      amount,
      frequency,
      inflationAdjusted,
      start,
      end,
    }
  })
}

const pascalToTitleCase = (str: string): string => {
  // Insert a space before any uppercase letter that follows a lowercase letter
  const spacedStr = str.replace(/([a-z])([A-Z])/g, "$1 $2")

  // Split the string into words
  return spacedStr
    .split(" ")
    .map(word => {
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    })
    .join(" ")
}

const ordinalSuffix = (n: number): string => {
  const j = n % 10,
    k = n % 100
  if (j === 1 && k !== 11) {
    return `${n}st`
  }
  if (j === 2 && k !== 12) {
    return `${n}nd`
  }
  if (j === 3 && k !== 13) {
    return `${n}rd`
  }
  return `${n}th`
}

const formatZodErrors = (
  errorObj: DeepReadonly<ZodErrorObject>,
  path: readonly string[] = []
): string[] => {
  let messages: string[] = []

  // If there are errors at the current level, build the friendly path and append the error messages.
  if (errorObj._errors && errorObj._errors.length > 0) {
    // AIDEV-NOTE: A trailing "root" names no field: the allocation-sum issue carries it so the
    // form can keep that error beside the per-field ones (see allocationSchema). Dropping it
    // keeps the upload message reading "Initial Assets > Allocation > ...". Only the last
    // segment is dropped, so a field that happened to be called "root" mid-path would survive.
    const fieldPath = path[path.length - 1] === "root" ? path.slice(0, -1) : path
    const filteredPath = fieldPath.map((p, idx) => {
      return fieldPath[idx - 1] === "cashFlows" && !isNaN(Number(p))
        ? ordinalSuffix(Number(p) + 1)
        : p
    })

    let friendlyPath = filteredPath.map(pascalToTitleCase).join(" > ")
    const fieldMapping: Record<string, string> = {
      // Asset field mappings
      "Spx Tr Pct": "Stocks: S&P 500",
      "World Ex Us Equities Pct": "Stocks: Non-US",
      "Cash Pct": "Cash: 3m U.S. T-bills",
      "Bm10y Pct": "Bonds: 10y U.S. Treasury",
      "Bm30y Pct": "Bonds: 30y U.S. Treasury",
      "Commodities Pct": "Commodities",
      "Gold Pct": "Gold",
      "Small Stocks Pct": "Small Stocks (SMB)",
      "Value Stocks Pct": "Value Stocks (HML)",
      "Expense Ratio Per Annum Pct": "Expense Ratio",

      // General Parameters field mappings
      "Initial Portfolio Dollars": "Initial Portfolio Total",
      "Retirement Horizon Months": "Retirement Horizon",
      "Final Value Pct Of Initial": "Final Value Target",
      "Inflation Per Annum Pct": "Inflation Rate",
      "Initial Consumption Per Annum Dollars": "Initial Consumption Amount",
      "Glide Path Length Months": "Glide Path Length",
    }

    const regex = new RegExp(Object.keys(fieldMapping).join("|"), "g")

    friendlyPath = friendlyPath.replace(regex, match => fieldMapping[match])

    errorObj._errors.forEach((errMsg: string) => {
      messages.push(friendlyPath ? `${friendlyPath} > ${errMsg}` : errMsg)
    })
  }

  // Recurse into nested objects
  for (const key in errorObj) {
    if (key === "_errors") continue
    messages = messages.concat(
      formatZodErrors(errorObj[key] as DeepReadonly<ZodErrorObject>, [...path, key])
    )
  }

  return messages.sort()
}

const parseSheet = (sheet: DeepReadonly<XLSX.WorkSheet>): SheetData => {
  return XLSX.utils.sheet_to_json<CellValue[]>(sheet as XLSX.WorkSheet, {
    header: 1,
    blankrows: false,
    defval: null,
    raw: true,
    dateNF: "m/yyyy",
  })
}

export const parseWorkbookV1 = (
  workbook: DeepReadonly<XLSX.WorkBook>
): UserParametersV1 | string[] => {
  const requiredSheets = ["General Parameters", "Assets", "Cash Flows"]
  const missingSheets = requiredSheets.filter(sheet => !workbook.SheetNames.includes(sheet))

  if (missingSheets.length > 0) {
    const sheets = missingSheets.length === 1 ? "sheet" : "sheets"
    return [`Missing required ${sheets}: ${missingSheets.join("; ")}.`]
  }

  try {
    const generalParametersData = parseSheet(workbook.Sheets["General Parameters"])
    const assetAllocationsData = parseSheet(workbook.Sheets.Assets)
    const cashFlowsData = parseSheet(workbook.Sheets["Cash Flows"])

    const generalParameters = parseGeneralParameters(generalParametersData)
    const { initialAssets, finalAssets } = parseAssetAllocations(assetAllocationsData)
    const cashFlows = parseCashFlows(cashFlowsData)

    const userParamsRaw = {
      ...generalParameters,
      initialAssets,
      finalAssets,
      cashFlows,
    }

    const parseResult = userParametersSchemaV1.safeParse(userParamsRaw)
    if (!parseResult.success) {
      return formatZodErrors(parseResult.error.format())
    } else {
      return parseResult.data
    }
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : "An unknown error occurred during parsing."

    // Report parsing logic errors (not user validation errors)
    Sentry.captureException(error, {
      tags: { errorType: "workbook_parsing_error", version: "v1" },
      extra: { workbookSheets: Object.keys(workbook.Sheets) },
    })

    return [errorMessage]
  }
}
